/**
 * Janelas de decisão no estilo do Business Tour: comprar/construir (bandeirola a hotel),
 * tomar a cidade de outro, cartas com alvo, dívida, e a ficha de cada casa.
 */
import { useState } from 'react';
import {
  type Choice,
  type Level,
  type Prompt,
  type PublicState,
  GROUPS,
  RESORT_PRICE,
  RESORT_RENTS,
  RESORT_SQUARES,
  baseRent,
  buildQuote,
  forcedPrice,
  levelCost,
  maxLevelFor,
  sellValue,
  square as squareOf,
  HAND_LIMIT,
  MST_IDLE_ROUNDS,
  SHOP,
  TAKEOVER_MULT,
  takeoverPrice,
  valueOf,
} from '@tigrinho/engine';
import { buildingArt, squareIcon } from '../lib/art';
import { ART, CARD_ART } from '../lib/art';
import { CARD_TEXT, DECK_LABEL, GROUP_COLORS, HELD_EMOJI, LEVEL_NAMES, SEAT_COLORS, SHOP_TEXT, SPECIAL_LINES } from '../lib/content';
import { moneyNumber } from '../lib/format';
import { useHold, useRoomCtx } from '../screens/RoomScreen';
import { useCountdown } from './Hud';
import { asState, decision, effectsOn, myProps } from './selectors';
import { Sheet } from './Sheet';

const RESORT_COLOR = '#16a3a3';

/** Aluguel que a cidade cobraria com este dono e este nível (grupo, festa e Copa incluídos). */
function rentAt(g: PublicState, index: number, owner: string, level: number): number {
  const props = [...g.props];
  props[index] = { owner, level: level as Level };
  return baseRent(asState({ ...g, props }), index);
}

function Secs() {
  const cd = useCountdown();
  if (!cd) return null;
  return <span className="pill-secs">{Math.ceil(cd.left / 1000)}</span>;
}

/** A decisão da vez, se for minha. */
export function PromptDialog() {
  const { room, you } = useRoomCtx();
  const held = useHold();
  const g = room.game!;
  const d = decision(g);
  // Primeiro o peão chega na casa; só depois a janela aparece.
  if (!you || g.phase === 'finished' || held) return null;
  if (d.kind === 'debt') return d.player === you ? <DebtDialog amount={d.amount} creditor={d.creditor} /> : null;
  if (d.kind !== 'prompt' || d.prompt.player !== you) return null;
  const p = d.prompt;
  if (p.kind === 'build') return <BuildDialog key={`${p.square}:${p.from}`} prompt={p} />;
  if (p.kind === 'takeover') return <TakeoverDialog prompt={p} />;
  if (p.kind === 'card') return <CardDialog prompt={p} />;
  if (p.kind === 'tax') return <TaxDialog prompt={p} />;
  if (p.kind === 'mst') return <MstDialog prompt={p} />;
  return null; // Volta ao Mundo e Copa: escolha no tabuleiro
}

// ---------------------------------------------------------------------------

/**
 * "Faz o L": a carta mais fofa do jogo, toda rosinha e cheia de coração... para cobrar o
 * imposto mais pesado. Paga direitinho ou arrisca a sonegação nos dados.
 */
function TaxDialog({ prompt }: { prompt: Extract<Prompt, { kind: 'tax' }> }) {
  const { act, money, room, you } = useRoomCtx();
  const me = room.game!.players.find((p) => p.id === you)!;
  return (
    <div className="love-wrap" role="dialog" aria-modal="true" aria-labelledby="love-title">
      <div className="love-card">
        <img className="love-art" src={ART.love} alt="" />
        <h2 id="love-title">Governo do Amor</h2>
        <p className="love-lead">
          O amor cobra <b>25%</b> do valor das suas propriedades.
        </p>
        <p className="love-amount">{money(prompt.amount)}</p>
        <div className="love-actions">
          <button className="pill pill-big" onClick={() => act({ type: 'PAY_TAX' })}>
            Pagar com amor <Secs />
          </button>
          <button className="pill pill-gray" onClick={() => act({ type: 'EVADE_TAX' })} disabled={me.cash < prompt.evaded}>
            Sonegar 🎲
          </button>
        </div>
        <ul className="love-rules">
          <li>
            <b>8 ou mais</b> nos dados: paga só {money(prompt.evaded)}
          </li>
          <li>
            <b>Menos de 8</b>: paga {money(prompt.caught)} e fica 3 rodadas preso (fiança de 600 mil)
          </li>
        </ul>
        <p className="love-foot">♥ o amor venceu ♥</p>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------

function BuildDialog({ prompt }: { prompt: Extract<Prompt, { kind: 'build' }> }) {
  const { room, you, act, square, money } = useRoomCtx();
  const g = room.game!;
  const me = g.players.find((p) => p.id === you)!;
  const i = prompt.square;
  const sq = squareOf(i);

  if (sq.kind === 'resort') {
    const mine = RESORT_SQUARES.filter((r) => g.props[r]?.owner === you).length;
    return (
      <Sheet title={square(i)} color={RESORT_COLOR} onClose={() => act({ type: 'SKIP_BUILD' })}>
        <div className="deal">
          <div className="deal-art">
            <img src={squareIcon(i) ?? ''} alt="" />
          </div>
          <div className="deal-info">
            <p className="deal-lead">Resort livre. Quanto mais resorts você tiver, mais caro fica o aluguel de todos eles.</p>
            <table className="levels">
              <thead>
                <tr>
                  <th>Resorts seus</th>
                  <th>Aluguel</th>
                </tr>
              </thead>
              <tbody>
                {RESORT_RENTS.map((r, k) => (
                  <tr key={k} className={k === mine ? 'is-on' : ''}>
                    <td>{k + 1}</td>
                    <td className="num">{moneyNumber(r)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="deal-note">Com os 4 resorts você vence na hora.</p>
          </div>
        </div>
        <div className="deal-actions">
          <button className="pill pill-blue pill-big" disabled={me.cash < RESORT_PRICE} onClick={() => act({ type: 'BUILD', level: 0 })}>
            Comprar por {money(RESORT_PRICE)}
          </button>
          <button className="pill pill-gray" onClick={() => act({ type: 'SKIP_BUILD' })}>
            Agora não <Secs />
          </button>
        </div>
      </Sheet>
    );
  }

  return <CityBuild prompt={prompt} />;
}

function CityBuild({ prompt }: { prompt: Extract<Prompt, { kind: 'build' }> }) {
  const { room, you, act, square, money } = useRoomCtx();
  const g = room.game!;
  const me = g.players.find((p) => p.id === you)!;
  const i = prompt.square;
  const sq = squareOf(i);
  const s = asState(g);
  const quote = (l: number) => buildQuote(s, me.id, i, l);
  const levels = [0, 1, 2, 3, 4];
  const affordable = levels.filter((l) => l > prompt.from && l <= prompt.max && quote(l).cost <= me.cash);
  const [pick, setPick] = useState<number>(affordable.length ? affordable[affordable.length - 1]! : prompt.from + 1);
  const cost = pick > prompt.from ? quote(pick).cost : 0;
  const can = affordable.includes(pick);
  const discounted = pick > prompt.from && quote(pick).discounted;

  const lockReason = (l: number): string | null => {
    if (l <= prompt.from || l <= prompt.max) return null;
    return 'precisa da casa 3 e cair aqui de novo';
  };

  return (
    <Sheet title={square(i)} color={GROUP_COLORS[sq.group!]} onClose={() => act({ type: 'SKIP_BUILD' })}>
      <div className="deal">
        <div className="deal-art">
          {pick <= 0 ? <BigFlag color={SEAT_COLORS[room.seats.findIndex((x) => x.id === you)] ?? '#1f6bff'} /> : <img src={buildingArt(sq.group!, pick)} alt="" />}
          <span className="deal-art-label">{LEVEL_NAMES[Math.max(0, pick)]}</span>
        </div>
        <div className="deal-info">
          <table className="levels is-pickable">
            <thead>
              <tr>
                <th aria-label="Situação" />
                <th>Construção</th>
                <th>Custa agora</th>
                <th>Aluguel</th>
              </tr>
            </thead>
            <tbody>
              {levels.map((l) => {
                const owned = l <= prompt.from;
                const lock = lockReason(l);
                const q = owned ? 0 : quote(l).cost;
                const poor = !owned && !lock && q > me.cash;
                const on = l === pick;
                return (
                  <tr
                    key={l}
                    className={[owned ? 'is-owned' : '', lock ? 'is-locked' : '', poor ? 'is-poor' : '', on ? 'is-on' : ''].join(' ')}
                    onClick={() => !owned && !lock && !poor && setPick(l)}
                  >
                    <td className="tick">{owned ? '✓' : on ? '●' : ''}</td>
                    <td>{LEVEL_NAMES[l]}</td>
                    <td className="num">{owned ? 'já tem' : lock ? <small>{lock}</small> : moneyNumber(q)}</td>
                    <td className="num">{moneyNumber(rentAt(g, i, me.id, l))}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <p className="deal-note">Valores em contos.</p>
          {pick > prompt.from && (
            <p className="deal-note">
              Se alguém tomar de você: {money(valueOf(i, pick) * TAKEOVER_MULT)} (4 vezes o que você investiu)
              {discounted && ' · Black Friday: construção pela metade'}
            </p>
          )}
        </div>
      </div>
      <div className="deal-actions">
        <button className="pill pill-blue pill-big" disabled={!can} onClick={() => act({ type: 'BUILD', level: pick })}>
          {prompt.from < 0 ? (pick === 0 ? 'Colocar bandeirola' : 'Comprar') : 'Construir'} · {money(cost)}
        </button>
        <button className="pill pill-gray" onClick={() => act({ type: 'SKIP_BUILD' })}>
          Agora não <Secs />
        </button>
      </div>
    </Sheet>
  );
}

function BigFlag({ color }: { color: string }) {
  return (
    <svg className="big-flag" viewBox="0 0 40 60" aria-hidden="true">
      <ellipse cx="9" cy="57" rx="8" ry="2.6" fill="rgba(0,0,0,.18)" />
      <rect x="7.2" y="6" width="3.6" height="51" rx="1.8" fill="#6b4b2a" />
      <circle cx="9" cy="5.5" r="4" fill="#ffd43b" stroke="#c99a00" strokeWidth="1" />
      <path d="M11 9 C 20 6, 27 13, 38 9 L 38 31 C 27 35, 20 28, 11 31 Z" fill={color} stroke="#fff" strokeWidth="2" strokeLinejoin="round" />
    </svg>
  );
}

// ---------------------------------------------------------------------------

function TakeoverDialog({ prompt }: { prompt: Extract<Prompt, { kind: 'takeover' }> }) {
  const { room, you, act, square, money, name, seatIndex } = useRoomCtx();
  const g = room.game!;
  const me = g.players.find((p) => p.id === you)!;
  const o = g.props[prompt.square]!;
  const sq = squareOf(prompt.square);
  return (
    <Sheet title={`Tomar ${square(prompt.square)}?`} color={SEAT_COLORS[seatIndex(o.owner)]} onClose={() => act({ type: 'SKIP_TAKEOVER' })}>
      <div className="deal">
        <div className="deal-art">
          {o.level === 0 ? <BigFlag color={SEAT_COLORS[seatIndex(o.owner)]!} /> : <img src={buildingArt(sq.group!, o.level)} alt="" />}
          <span className="deal-art-label">{LEVEL_NAMES[o.level]}</span>
        </div>
        <div className="deal-info">
          <p className="deal-lead">
            Essa cidade é de <strong>{name(o.owner)}</strong>. Você pode comprar dela por 4 vezes o que foi investido, e o dinheiro vai todo para{' '}
            {name(o.owner)}.
          </p>
          <dl className="facts">
            <div>
              <dt>Preço para tomar</dt>
              <dd className="num">{money(prompt.price)}</dd>
            </div>
            <div>
              <dt>Aluguel dela hoje</dt>
              <dd className="num">{money(rentAt(g, prompt.square, me.id, o.level))}</dd>
            </div>
          </dl>
          <p className="deal-note">Depois de tomar, você ainda pode construir mais nela.</p>
        </div>
      </div>
      <div className="deal-actions">
        <button className="pill pill-red pill-big" disabled={me.cash < prompt.price} onClick={() => act({ type: 'TAKEOVER' })}>
          Tomar por {money(prompt.price)}
        </button>
        <button className="pill pill-gray" onClick={() => act({ type: 'SKIP_TAKEOVER' })}>
          Deixar quieto <Secs />
        </button>
      </div>
    </Sheet>
  );
}

// ---------------------------------------------------------------------------

/** Lojinha de cartas: compra na sua vez, antes de rolar. No máximo 3 cartas na mão. */
export function ShopSheet({ onClose }: { onClose: () => void }) {
  const { room, you, act, money } = useRoomCtx();
  const g = room.game!;
  const me = g.players.find((p) => p.id === you);
  const d = decision(g);
  const hand = me ? me.held.length + (me.shop?.length ?? 0) : 0;
  const open = !!me && d.kind === 'turn' && d.player === you && d.phase === 'roll';
  return (
    <Sheet title="Lojinha" color="#14a13f" onClose={onClose}>
      <p className="shop-lead">
        {open ? 'Compre antes de rolar os dados.' : 'A lojinha abre na sua vez, antes de rolar os dados.'} Preços em contos. Sua mão:{' '}
        <b>
          {hand}/{HAND_LIMIT}
        </b>
      </p>
      <ul className="shop-list">
        {(Object.keys(SHOP) as (keyof typeof SHOP)[]).map((c) => {
          const price = SHOP[c];
          const can = open && hand < HAND_LIMIT && (me?.cash ?? 0) >= price;
          return (
            <li key={c} className="shop-item">
              <span className="shop-emoji" aria-hidden="true">
                {HELD_EMOJI[c]}
              </span>
              <span className="shop-info">
                <b>{SHOP_TEXT[c]?.title}</b>
                <small>{SHOP_TEXT[c]?.text}</small>
              </span>
              <button className="pill pill-green shop-buy" disabled={!can} onClick={() => act({ type: 'BUY_CARD', card: c })} aria-label={`Comprar por ${money(price)}`}>
                {moneyNumber(price)}
              </button>
            </li>
          );
        })}
      </ul>
      <div className="deal-actions">
        <button className="pill pill-gray" onClick={onClose}>
          Fechar
        </button>
      </div>
    </Sheet>
  );
}

// ---------------------------------------------------------------------------

/** O MST chegou numa cidade sua parada: chamar o fazendeiro (caro) ou perder um nível. */
function MstDialog({ prompt }: { prompt: Extract<Prompt, { kind: 'mst' }> }) {
  const { room, you, act, square, money } = useRoomCtx();
  const g = room.game!;
  const me = g.players.find((p) => p.id === you)!;
  const o = g.props[prompt.square]!;
  return (
    <Sheet title="O MST invadiu!" color="#d7192b" onClose={() => act({ type: 'ACCEPT_MST' })}>
      <div className="deal">
        <div className="deal-art">
          <img src={ART.mst} alt="" />
        </div>
        <div className="deal-info">
          <p className="deal-lead">
            Ninguém pagou aluguel em <strong>{square(prompt.square)}</strong> por {MST_IDLE_ROUNDS} rodadas e a turma do MST montou acampamento.
          </p>
          <dl className="facts">
            <div>
              <dt>Se deixar, fica com</dt>
              <dd>{LEVEL_NAMES[o.level - 1]}</dd>
            </div>
            <div>
              <dt>Fazendeiro com a liminar</dt>
              <dd className="num">{money(prompt.cost)}</dd>
            </div>
          </dl>
          <p className="deal-note">Dica: o Fazendeiro da lojinha expulsa a próxima invasão de graça, e a Carta Fazendeiro (Legaaaal) protege uma cidade para sempre.</p>
        </div>
      </div>
      <div className="deal-actions">
        <button className="pill pill-yellow pill-big" disabled={me.cash < prompt.cost} onClick={() => act({ type: 'EXPEL_MST' })}>
          Fazendeiro · {money(prompt.cost)}
        </button>
        <button className="pill pill-gray" onClick={() => act({ type: 'ACCEPT_MST' })}>
          Deixar invadir <Secs />
        </button>
      </div>
    </Sheet>
  );
}

// ---------------------------------------------------------------------------

function CardDialog({ prompt }: { prompt: Extract<Prompt, { kind: 'card' }> }) {
  const { room, act, square, name, money } = useRoomCtx();
  const g = room.game!;
  const c = CARD_TEXT[prompt.card];
  const deck = c?.deck ?? 'sorte';
  const label = (ch: Choice) => {
    if (ch.square === undefined) return { main: name(ch.player!), sub: '' };
    const o = g.props[ch.square];
    const parts = [ch.player ? `de ${name(ch.player)}` : o ? `de ${name(o.owner)}` : 'livre'];
    if (prompt.card === 'compraForcada') parts.push(`por ${money(forcedPrice(ch.square))}`);
    else if (o && squareOf(ch.square).kind === 'city') parts.push(LEVEL_NAMES[o.level]);
    return { main: square(ch.square), sub: parts.join(' · ') };
  };
  return (
    <Sheet title={c?.title ?? prompt.card} tone={deck}>
      <div className="card-ask">
        <img className="card-ask-art" src={CARD_ART[deck]} alt="" />
        <div>
          <p className="card-deck">{DECK_LABEL[deck]}</p>
          <p className="card-text">{c?.text}</p>
          {prompt.picksLeft > 1 && <p className="deal-note">Escolha até {prompt.picksLeft}.</p>}
        </div>
      </div>
      <div className="choice-list">
        {prompt.choices.map((ch) => {
          const l = label(ch);
          return (
            <button key={ch.id} className="choice" onClick={() => act({ type: 'CHOOSE', choice: ch.id })}>
              <strong>{l.main}</strong>
              {l.sub && <span>{l.sub}</span>}
            </button>
          );
        })}
      </div>
      <div className="deal-actions">
        {prompt.canSkip && (
          <button className="pill pill-gray" onClick={() => act({ type: 'CHOOSE', choice: 'skip' })}>
            {prompt.picked.length ? 'Parar por aqui' : 'Não usar'} <Secs />
          </button>
        )}
      </div>
    </Sheet>
  );
}

// ---------------------------------------------------------------------------

function DebtDialog({ amount, creditor }: { amount: number; creditor: string }) {
  const { room, you, act, name, money, square } = useRoomCtx();
  const g = room.game!;
  const me = g.players.find((p) => p.id === you)!;
  const [confirm, setConfirm] = useState(false);
  const missing = amount - me.cash;
  const mine = myProps(g, me.id).sort((a, b) => sellValue(asState(g), a) - sellValue(asState(g), b));
  const total = mine.reduce((t, i) => t + sellValue(asState(g), i), 0);
  return (
    <Sheet title="Hora de pagar" tone="danger">
      <p className="deal-lead">
        Você deve <strong>{money(amount)}</strong> {creditor === 'bank' ? 'ao banco' : `para ${name(creditor)}`}.{' '}
        {missing > 0 ? `Faltam ${money(missing)}: venda cidades ao banco pela metade do valor.` : 'Você já tem o suficiente.'}
      </p>
      {mine.length > 0 && (
        <div className="sell-list">
          {mine.map((i) => {
            const o = g.props[i]!;
            return (
              <div key={i} className="sell-row" style={{ '--band': squareOf(i).group ? GROUP_COLORS[squareOf(i).group!] : RESORT_COLOR } as React.CSSProperties}>
                <span className="sell-name">
                  {square(i)}
                  <small>{squareOf(i).kind === 'resort' ? 'Resort' : LEVEL_NAMES[o.level]}</small>
                </span>
                <button className="pill pill-yellow pill-small" onClick={() => act({ type: 'SELL', square: i })}>
                  Vender +{money(sellValue(asState(g), i))}
                </button>
              </div>
            );
          })}
        </div>
      )}
      <div className="deal-actions">
        <button className="pill pill-blue pill-big" disabled={missing > 0} onClick={() => act({ type: 'PAY_DEBT' })}>
          Pagar {money(amount)} <Secs />
        </button>
        {missing > 0 && total >= missing && (
          <button className="pill pill-yellow" onClick={() => act({ type: 'AUTO_SETTLE_DEBT' })}>
            Vender o mínimo e pagar
          </button>
        )}
        {missing > 0 &&
          (confirm ? (
            <>
              <button className="pill pill-red" onClick={() => act({ type: 'DECLARE_BANKRUPTCY' })}>
                Falir de verdade
              </button>
              <button className="pill pill-gray" onClick={() => setConfirm(false)}>
                Voltar
              </button>
            </>
          ) : (
            <button className="pill pill-gray" onClick={() => setConfirm(true)}>
              Declarar falência
            </button>
          ))}
      </div>
      {confirm && <p className="deal-note is-bad">Falindo, você sai da partida e suas cidades voltam para o banco.</p>}
    </Sheet>
  );
}

// ---------------------------------------------------------------------------

/** Ficha de uma casa (tocar no tabuleiro). */
export function PropertySheet({ index, onClose }: { index: number; onClose: () => void }) {
  const { room, you, square, name, money, seatIndex } = useRoomCtx();
  const g = room.game!;
  const sq = squareOf(index);
  const o = g.props[index];
  const fx = effectsOn(g, index);
  const festival = g.festivals.includes(index);
  const champ = g.championship?.square === index ? g.championship.mult : 0;

  if (sq.kind !== 'city' && sq.kind !== 'resort') {
    return (
      <Sheet title={square(index)} onClose={onClose}>
        <div className="deal">
          {squareIcon(index) && (
            <div className="deal-art">
              <img src={squareIcon(index)!} alt="" />
            </div>
          )}
          <p className="deal-lead">{SPECIAL_LINES[sq.kind] ?? ''}</p>
        </div>
      </Sheet>
    );
  }

  const owner = o?.owner;
  const ownerTag = owner ? (
    <span className="owner-tag" style={{ '--seat': SEAT_COLORS[seatIndex(owner)] } as React.CSSProperties}>
      {owner === you ? 'Você' : name(owner)}
    </span>
  ) : (
    'Ninguém ainda'
  );

  if (sq.kind === 'resort') {
    const n = owner ? RESORT_SQUARES.filter((r) => g.props[r]?.owner === owner).length : 0;
    return (
      <Sheet title={square(index)} color={RESORT_COLOR} onClose={onClose}>
        <div className="deal">
          <div className="deal-art">
            <img src={squareIcon(index)!} alt="" />
          </div>
          <div className="deal-info">
            <dl className="facts">
              <div>
                <dt>Dono</dt>
                <dd>{ownerTag}</dd>
              </div>
              <div>
                <dt>Preço</dt>
                <dd className="num">{money(RESORT_PRICE)}</dd>
              </div>
            </dl>
            <table className="levels">
              <thead>
                <tr>
                  <th>Resorts do dono</th>
                  <th>Aluguel</th>
                </tr>
              </thead>
              <tbody>
                {RESORT_RENTS.map((r, k) => (
                  <tr key={k} className={k + 1 === n ? 'is-on' : ''}>
                    <td>{k + 1}</td>
                    <td className="num">{moneyNumber(r)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="deal-note">Resort não pode ser tomado. Com os 4, o dono vence na hora.</p>
          </div>
        </div>
      </Sheet>
    );
  }

  const groupSize = GROUPS[sq.group!]!.length;
  const who = owner ?? you ?? '';
  return (
    <Sheet title={square(index)} color={GROUP_COLORS[sq.group!]} onClose={onClose}>
      <div className="deal">
        <div className="deal-art">
          {o && o.level > 0 ? (
            <img src={buildingArt(sq.group!, o.level)} alt="" />
          ) : (
            <BigFlag color={owner ? SEAT_COLORS[seatIndex(owner)]! : '#c9c2b4'} />
          )}
          <span className="deal-art-label">{o ? LEVEL_NAMES[o.level] : 'Livre'}</span>
        </div>
        <div className="deal-info">
          <dl className="facts">
            <div>
              <dt>Dono</dt>
              <dd>{ownerTag}</dd>
            </div>
            <div>
              <dt>{o ? 'Aluguel agora' : 'Terreno'}</dt>
              <dd className="num">{money(o ? baseRent(asState(g), index) : levelCost(index, 0))}</dd>
            </div>
            {o && o.level < 4 && (
              <div>
                <dt>Para tomar</dt>
                <dd className="num">{money(takeoverPrice(asState(g), index))}</dd>
              </div>
            )}
          </dl>
          <table className="levels">
            <thead>
              <tr>
                <th>Construção</th>
                <th>Custo</th>
                <th>Aluguel</th>
              </tr>
            </thead>
            <tbody>
              {[0, 1, 2, 3, 4].map((l) => (
                <tr key={l} className={o?.level === l ? 'is-on' : ''}>
                  <td>{LEVEL_NAMES[l]}</td>
                  <td className="num">{moneyNumber(levelCost(index, l))}</td>
                  <td className="num">{moneyNumber(rentAt(g, index, who, l))}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="deal-note">
            Grupo de {groupSize} cidades: fechando o grupo, o aluguel dobra.
            {festival && ' Evento: aluguel 1,7 vez maior a partida toda.'}
            {champ > 0 && ` Copa: aluguel x${champ}.`}
            {fx.length > 0 && ' Interditada nesta rodada.'}
            {o?.level === 4 && ' Hotel não pode ser tomado.'}
            {you && !o && ` Você pode subir até ${LEVEL_NAMES[maxLevelFor(asState(g), you, index)]} na compra.`}
          </p>
        </div>
      </div>
    </Sheet>
  );
}
